import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, ShieldAlert } from "lucide-react";
import { PortalHeader } from "@/components/portal/ClientTimeline";
import { AdminToday } from "@/components/portal/AdminToday";
import { AdminClients } from "@/components/portal/AdminClients";
import { AdminChecklist } from "@/components/portal/AdminChecklist";
import { AdminApprovals } from "@/components/portal/AdminApprovals";
import { AdminOutbound } from "@/components/portal/AdminOutbound";
import { AdminReports } from "@/components/portal/AdminReports";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = ["today", "clients", "checklist", "approvals", "reports"];

export default function PortalAdminPage() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("today");
  const [client, setClient] = useState(null);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  const handleAdminLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-[#060D1A] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#060D1A] flex items-center justify-center px-4" data-testid="portal-admin-login">
        <div className="w-full max-w-md rounded-3xl border border-white/15 bg-[#0F1B31]/80 p-10 text-center">
          <p className="font-mono-label text-xs uppercase tracking-[0.3em] text-slate-400 mb-6">Staff Only</p>
          <h1 className="font-display text-2xl font-bold text-white">OG Solutions Admin</h1>
          <p className="mt-3 text-sm text-slate-400">This console is restricted to OG Solutions staff.</p>
          <button
            data-testid="portal-admin-google-button"
            onClick={handleAdminLogin}
            className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-white px-8 py-4 text-base font-semibold text-[#060D1A] transition-colors duration-300 hover:bg-slate-300"
          >
            Staff sign-in with Google
          </button>
        </div>
      </div>
    );
  }

  if (user.role !== "admin") {
    return (
      <div className="min-h-screen bg-[#060D1A] flex items-center justify-center px-4" data-testid="portal-admin-restricted">
        <div className="w-full max-w-md rounded-3xl border border-white/15 bg-[#0F1B31]/80 p-10 text-center">
          <ShieldAlert className="h-10 w-10 text-slate-500 mx-auto mb-4" />
          <h1 className="font-display text-2xl font-bold text-white">Restricted area</h1>
          <p className="mt-3 text-sm text-slate-400">This account doesn't have staff access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#060D1A] text-slate-100" data-testid="portal-admin-page">
      <PortalHeader user={user} right={
        <span className="font-mono-label text-[10px] uppercase tracking-[0.25em] rounded-full border border-white/25 px-3 py-1.5 text-white">
          Admin Console
        </span>
      } />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="portal-admin-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`rounded-full px-5 py-2 text-sm font-semibold capitalize transition-colors duration-300 ${
                tab === t ? "bg-white text-[#060D1A]" : "border border-white/15 text-slate-300 hover:border-white/40"
              }`}
            >
              {t === "today" ? "Today's Client Work" : t}
            </button>
          ))}
        </div>
        {tab === "today" && <AdminToday />}
        {tab === "clients" && <AdminClients selected={client} onSelect={setClient} />}
        {tab === "checklist" && <AdminChecklist client={client} />}
        {tab === "approvals" && (<><AdminApprovals /><AdminOutbound /></>)}
        {tab === "reports" && <AdminReports client={client} />}
      </main>
    </div>
  );
}
